import { 
  Row, 
  BondRow, 
  BondRowItem, 
  CurrencyRowItem, 
  AttachButtonContainer, 
  DeleteRowButton, 
  FileCard, 
  FileName, 
  DeleteButton, 
  BottomFieldsContainer 
} from "../styles";
import Input from "../../../../../components/inputField";
import { StyledQuestion } from "../../styles";
import React, { useState, useRef, useMemo } from "react";
import { FunctionalButton } from "../../../../../components/functionalButton";
import { CustomDropdown } from "../CustomDropdown";
import { ESubInfo } from "../types";
import bondTypes from "../bondTypes.json";
import { COLORS } from "../../../../../constants";

// Currency options for the dropdown
const currencyOptions = [
  { label: "CAD", value: "CAD" },
  { label: "USD", value: "USD" }
];

type BondInfo = {
  id: string;
  bond_type: string;
  bond_currency: string;
  lm_bond_amount: string;
  attached_file?: File | null;
  file_name?: string;
};

type ValidationErrors = {
  bondAmount: { [key: string]: string };
  bondType: { [key: string]: string };
  bondCurrency: { [key: string]: string };
  retainage: string;
  maintenancePeriod: string;
};

type BondInfoSectionProps = {
  esubData: ESubInfo;
  setESubData: (field: string, value: string) => void;
  isMobile: boolean;
  handleNext: () => void;
  panel: number;
  setPanel: (panel: number) => void;
  setIsLastPanel: (isLastPanel: boolean) => void;
  setPreviousStepPanel: (prevStepPanel: number) => void;
};

export const BondInfoSection = ({
  esubData,
  setESubData,
  isMobile,
  handleNext,
  panel,
  setPanel,
  setIsLastPanel,
  setPreviousStepPanel,
}: BondInfoSectionProps) => {
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const formatNumberWithCommas = (value: string): string => {
    if (!value) return "";
    
    const cleanValue = value.replace(/[^\d.]/g, "");
    
    if (cleanValue.includes(".")) {
      const [wholePart, decimalPart] = cleanValue.split(".");
      const formattedWhole = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${formattedWhole}.${decimalPart}`;
    }
    return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const removeCommas = (value: string): string => {
    return value.replace(/,/g, "");
  };

  // Validation functions
  const validateBondType = (value: string): string => {
    if (!value || value.trim() === "") {
      return "Bond type is required";
    }
    return "";
  };

  const validateBondCurrency = (value: string): string => {
    if (!value || value.trim() === "") {
      return "Bond currency is required";
    }
    return "";
  };

  const validateBondAmount = (value: string): string => {
    if (!value || value.trim() === "") {
      return "Bond amount is required";
    }
    
    const numericValue = parseFloat(removeCommas(value));
    if (isNaN(numericValue)) {
      return "Bond amount must be a valid number";
    }
    if (numericValue < 0) {
      return "Bond amount cannot be negative";
    }
    return "";
  };

  const [bondRows, setBondRows] = useState<BondInfo[]>([
    {
      id: '1',
      bond_type: esubData?.bond_details?.bond_type ?? "",
      bond_currency: esubData?.bond_details?.bond_currency ?? "",
      lm_bond_amount: esubData?.bond_details?.lm_bond_amount ?? "",
      attached_file: null,
      file_name: ""
    }
  ]);
  
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(() => {
    // Initialize with validation for the first bond row
    const initialBondType = esubData?.bond_details?.bond_type ?? "";
    const initialBondCurrency = esubData?.bond_details?.bond_currency ?? "";
    const initialBondAmount = esubData?.bond_details?.lm_bond_amount ?? "";
    
    return {
      bondAmount: { '1': validateBondAmount(initialBondAmount) },
      bondType: { '1': validateBondType(initialBondType) },
      bondCurrency: { '1': validateBondCurrency(initialBondCurrency) },
      retainage: "",
      maintenancePeriod: ""
    };
  });

  const validateRetainage = (value: string): string => {
    if (!value) return "";
    
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return "Retainage must be a valid number";
    }
    if (numericValue < 0 || numericValue > 100) {
      return "Retainage must be between 0 and 100";
    }
    return "";
  };

  const validateMaintenancePeriod = (value: string): string => {
    if (!value) return "";
    
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
      return "Maintenance period must be a valid number";
    }
    if (numericValue < 0 || numericValue > 10) {
      return "Maintenance period must be between 0 and 10 years";
    }
    return "";
  };

  const handleBondRowChange = (id: string, field: keyof BondInfo, value: string) => {
    setBondRows(prev => prev.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
    
    // Validate all bond rows
    if (field === 'bond_type') {
      const error = validateBondType(value);
      setValidationErrors(prev => ({ 
        ...prev, 
        bondType: { ...prev.bondType, [id]: error }
      }));
      // Only update eSubData for the first row
      if (id === '1') {
        setESubData("bond_details.bond_type", value);
      }
    } else if (field === 'bond_currency') {
      const error = validateBondCurrency(value);
      setValidationErrors(prev => ({ 
        ...prev, 
        bondCurrency: { ...prev.bondCurrency, [id]: error }
      }));
      // Only update eSubData for the first row
      if (id === '1') {
        setESubData("bond_details.bond_currency", value);
      }
    } else if (field === 'lm_bond_amount') {
      const error = validateBondAmount(value);
      setValidationErrors(prev => ({ 
        ...prev, 
        bondAmount: { ...prev.bondAmount, [id]: error }
      }));
      // Only update eSubData for the first row
      if (id === '1') {
        setESubData("bond_details.lm_bond_amount", value);
      }
    }
  };

  const handleRetainageChange = (value: string) => {
    setESubData("bond_details.retainage", value);
    const error = validateRetainage(value);
    setValidationErrors(prev => ({ ...prev, retainage: error }));
  };

  const handleMaintenancePeriodChange = (value: string) => {
    setESubData("bond_details.maintenance_period", value);
    const error = validateMaintenancePeriod(value);
    setValidationErrors(prev => ({ ...prev, maintenancePeriod: error }));
  };

  const isFormValid = useMemo(() => {

    const hasBondAmountError = Object.values(validationErrors.bondAmount).some(error => error !== "");
    const hasBondTypeError = Object.values(validationErrors.bondType).some(error => error !== "");
    const hasBondCurrencyError = Object.values(validationErrors.bondCurrency).some(error => error !== "");
    const hasRetainageError = validationErrors.retainage !== "";
    const hasMaintenancePeriodError = validationErrors.maintenancePeriod !== "";
    

    const allBondRowsValid = bondRows.every(bondRow => 
      bondRow.bond_type.trim() !== "" && 
      bondRow.bond_currency.trim() !== "" && 
      bondRow.lm_bond_amount.trim() !== ""
    );
    
    return !hasBondAmountError && !hasBondTypeError && !hasBondCurrencyError && !hasRetainageError && !hasMaintenancePeriodError && allBondRowsValid;
  }, [validationErrors, bondRows]);

  const handleFileUpload = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === "application/pdf" || 
                 file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                 file.type === "application/msword")) {
      setBondRows(prev => prev.map(row => 
        row.id === id ? { ...row, attached_file: file, file_name: file.name } : row
      ));
    }
    event.target.value = '';
  };

  const handleDeleteFile = (id: string) => {
    setBondRows(prev => prev.map(row => 
      row.id === id ? { ...row, attached_file: null, file_name: "" } : row
    ));
  };

  const handleAttachClick = (id: string) => {
    fileInputRefs.current[id]?.click();
  };

  const addNewBondRow = () => {
    const newId = (bondRows.length + 1).toString();
    setBondRows(prev => [...prev, {
      id: newId,
      bond_type: "",
      bond_currency: "",
      lm_bond_amount: "",
      attached_file: null,
      file_name: ""
    }]);
    
    // Initialize validation errors for the new empty bond row
    setValidationErrors(prev => ({
      ...prev,
      bondType: { ...prev.bondType, [newId]: validateBondType("") },
      bondCurrency: { ...prev.bondCurrency, [newId]: validateBondCurrency("") },
      bondAmount: { ...prev.bondAmount, [newId]: validateBondAmount("") }
    }));
  };

  const removeBondRow = (id: string) => {
    if (bondRows.length > 1) {
      setBondRows(prev => prev.filter(row => row.id !== id));
    }
  };

  return (
    <>
      <StyledQuestion>What Are the Bond Details?</StyledQuestion>
      {bondRows.map((bondRow, index) => (
        <BondRow key={bondRow.id}>
          <BondRowItem style={{ minHeight: "80px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div>
              <CustomDropdown
                label="Bond Type"
                value={bondRow.bond_type}
                onChange={(value) => handleBondRowChange(bondRow.id, "bond_type", value)}
                options={bondTypes}
                placeholder="Select bond type"
                showSearch={true}
              />
              <div style={{ minHeight: "20px", marginTop: "4px" }}>
                {validationErrors.bondType[bondRow.id] && (
                  <div style={{ 
                    color: "#d32f2f", 
                    fontSize: "12px", 
                    marginLeft: "4px"
                  }}>
                    {validationErrors.bondType[bondRow.id]}
                  </div>
                )}
              </div>
            </div>
          </BondRowItem>
          
          <CurrencyRowItem style={{ minHeight: "80px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div>
              <CustomDropdown
                label="Bond Currency"
                value={bondRow.bond_currency}
                onChange={(value) => handleBondRowChange(bondRow.id, "bond_currency", value)}
                options={currencyOptions}
                placeholder="Select currency"
                showSearch={false}
              />
              <div style={{ minHeight: "20px", marginTop: "4px" }}>
                {validationErrors.bondCurrency[bondRow.id] && (
                  <div style={{ 
                    color: "#d32f2f", 
                    fontSize: "12px", 
                    marginLeft: "4px"
                  }}>
                    {validationErrors.bondCurrency[bondRow.id]}
                  </div>
                )}
              </div>
            </div>
          </CurrencyRowItem>
          
          <BondRowItem style={{ minHeight: "80px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div>
              <Input
                label="Bond Amount ($)"
                type="text"
                fontSize="18px"
                labelSize="12px"
                value={formatNumberWithCommas(bondRow.lm_bond_amount)}
                setValue={(e) => handleBondRowChange(bondRow.id, "lm_bond_amount", removeCommas(e))}
                backgroundColor=""
                color=""
                placeholder=""
                validateFunc={() => true}
                style={{}}
                withoutMargin={false}
                marginTop=""
                id=""
                labelWeight=""
                textAreaHeight=""
                borderColor=""
                autoComplete=""
                noCheckIcon={false}
                cancelIcon={false}
                noErrorBorder={false}
                hasError={validationErrors.bondAmount[bondRow.id] !== undefined && validationErrors.bondAmount[bondRow.id] !== ""}
                inputId=""
                disabled={false}
                width=""
                paddingRight=""
                paddingLeft=""
                onBlur={() => {}}
                showError={validationErrors.bondAmount[bondRow.id] !== undefined && validationErrors.bondAmount[bondRow.id] !== ""}
                readOnly={false}
                marginRight=""
                ref={null}
              />
              <div style={{ minHeight: "20px", marginTop: "4px" }}>
                {validationErrors.bondAmount[bondRow.id] && (
                  <div style={{ 
                    color: "#d32f2f", 
                    fontSize: "12px", 
                    marginLeft: "4px"
                  }}>
                    {validationErrors.bondAmount[bondRow.id]}
                  </div>
                )}
              </div>
            </div>
          </BondRowItem>
          
          <AttachButtonContainer style={{ minHeight: "80px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div>
              {!bondRow.attached_file ? (
                <FunctionalButton
                  buttonTitle="Attach Form"
                  handleButton={() => handleAttachClick(bondRow.id)}
                  width="100%"
                  height="48px"
                  fontSize="12px"
                  isReversedColor={undefined}
                  marginRight={undefined}
                  marginLeft={undefined}
                  marginTop="20px"
                  marginBottom={undefined}
                  disabled={undefined}
                  icon={undefined}
                  hasBoxShadow={undefined}
                  frontIcon={undefined}
                  weight={undefined}
                  borderColor={undefined}
                  selected={undefined}
                />
              ) : (
                <FileCard>
                  <FileName title={bondRow.file_name}>{bondRow.file_name}</FileName>
                  <DeleteButton onClick={() => handleDeleteFile(bondRow.id)}>×</DeleteButton>
                </FileCard>
              )}
              <div style={{ minHeight: "20px", marginTop: "4px" }}>
                {/* Empty space to maintain alignment with other fields */}
              </div>
            </div>
            <input
              type="file"
              ref={(el) => { fileInputRefs.current[bondRow.id] = el; }}
              style={{ display: "none" }}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => handleFileUpload(bondRow.id, e)}
            />
          </AttachButtonContainer>
          
          {bondRows.length > 1 && (
            <div style={{ minHeight: "80px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <DeleteRowButton onClick={() => removeBondRow(bondRow.id)}>
                ×
              </DeleteRowButton>
              <div style={{ minHeight: "20px", marginTop: "4px" }}>
                {/* Empty space to maintain alignment with other fields */}
              </div>
            </div>
          )}
        </BondRow>
      ))}

      <div style={{ width: "100%", marginTop: "4px", display: "flex", flexDirection: "row" }}>
        <div
          onClick={addNewBondRow}
          style={{
            color: COLORS.primaryOrange,
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            textAlign: "center",
            marginTop: "18px",
            marginRight: "16px"
          }}
        >
          + Add New Bond
        </div>
      </div>

      <BottomFieldsContainer>
        <Row>
          <div style={{ flex: 1, marginRight: "16px" }}>
            <Input
              label="Retainage (%)"
              type="text"
              fontSize="18px"
              labelSize="12px"
              value={esubData?.bond_details?.retainage}
              setValue={handleRetainageChange}
              backgroundColor=""
              color=""
              placeholder=""
              validateFunc={() => true}
              style={{}}
              withoutMargin={false}
              marginTop=""
              id=""
              labelWeight=""
              textAreaHeight=""
              borderColor=""
              autoComplete=""
              noCheckIcon={false}
              cancelIcon={false}
              noErrorBorder={false}
              hasError={validationErrors.retainage !== ""}
              inputId=""
              disabled={false}
              width=""
              paddingRight=""
              paddingLeft=""
              onBlur={() => {}}
              showError={validationErrors.retainage !== ""}
              readOnly={false}
              marginRight=""
              ref={null}
            />
            {validationErrors.retainage && (
              <div style={{ 
                color: "#d32f2f", 
                fontSize: "12px", 
                marginTop: "4px",
                marginLeft: "4px"
              }}>
                {validationErrors.retainage}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <Input
              label="Maintenance Period (Years)"
              type="text"
              fontSize="18px"
              labelSize="12px"
              value={esubData?.bond_details?.maintenance_period}
              setValue={handleMaintenancePeriodChange}
              backgroundColor=""
              color=""
              placeholder=""
              validateFunc={() => true}
              style={{}}
              withoutMargin={false}
              marginTop=""
              id=""
              labelWeight=""
              textAreaHeight=""
              borderColor=""
              autoComplete=""
              noCheckIcon={false}
              cancelIcon={false}
              noErrorBorder={false}
              hasError={validationErrors.maintenancePeriod !== ""}
              inputId=""
              disabled={false}
              width=""
              paddingRight=""
              paddingLeft=""
              onBlur={() => {}}
              showError={validationErrors.maintenancePeriod !== ""}
              readOnly={false}
              marginRight=""
              ref={null}
            />
            {validationErrors.maintenancePeriod && (
              <div style={{ 
                color: "#d32f2f", 
                fontSize: "12px", 
                marginTop: "4px",
                marginLeft: "4px"
              }}>
                {validationErrors.maintenancePeriod}
              </div>
            )}
          </div>
        </Row>
      </BottomFieldsContainer>

      <FunctionalButton
        buttonTitle="Next"
        handleButton={() => {
          handleNext();
          setPanel(6);
        }}
        width={undefined}
        height={undefined}
        isReversedColor={undefined}
        marginRight={undefined}
        marginLeft={undefined}
        marginTop={"40px"}
        marginBottom={undefined}
        fontSize={undefined}
        disabled={!isFormValid}
        icon={undefined}
        hasBoxShadow={undefined}
        frontIcon={undefined}
        weight={undefined}
        borderColor={undefined}
        selected={undefined}
      />
    </>
  );
};
